/**
 * 진행도 조회 — 학급 목록
 *
 * Lesson/Branch/{branch_id}에서 학급 조회
 * 학급·배정·강사 목록을 일괄 조회해 학급마다 추가 조회하지 않는다.
 * 학생 수: Assign/{branch_id}/Lesson/{lesson_id}.students 배열 길이
 */
import { useEffect, useState, useCallback } from "react";
import useFirestore from "shared/hooks/useFirestore";
import { QueryDocumentSnapshot } from "firebase/firestore";
import useGetBranchId from "shared/hooks/useGetBranchId";
import { useSortAndSearch } from "shared/hooks/useSortAndSearch";

interface Lesson {
    lesson_id: string;
    lesson_name: string;
    teacher_uid: string;
    teacher_name: string;
    student_count: number;
}

const useHomeworkClassView = () => {
    const { getBranchId } = useGetBranchId();
    const { readFirestore, readFirestoreWithQuery, readFirestoreOneDoc } = useFirestore();
    const [lessonList, setLessonList] = useState<Lesson[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);

    const getSearchText = useCallback((lesson: Lesson) => lesson.lesson_name, []);
    const getSortText = useCallback((lesson: Lesson) => lesson.lesson_name, []);
    const { searchKeyword, sortOrder, handleSearch, toggleSortOrder, filteredItems, setItems } =
        useSortAndSearch(lessonList, getSearchText, getSortText);

    useEffect(() => {
        getLessonList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getLessonList = async () => {
        setIsLoading(true);
        setLoadingProgress(0);
        try {
            const branch_id = await getBranchId();
            const [response, assignments, teachers] = await Promise.all([
                readFirestore(`Lesson/Branch/${branch_id}`),
                readFirestore(`Assign/${branch_id}/Lesson`),
                readFirestoreWithQuery("User/Auth/Teacher", {
                    field: "branch_id", operator: "==", data: branch_id
                })
            ]);
            for (const snapshot of [response, assignments, teachers]) {
                if (!snapshot || !Array.isArray(snapshot.docs)) {
                    throw snapshot instanceof Error ? snapshot : new Error("Invalid lesson list response");
                }
            }
            const doc_list: QueryDocumentSnapshot[] = response.docs;
            
            if (doc_list.length === 0) {
                setLessonList([]);
                setItems([]);
                return;
            }

            const studentCounts = new Map<string, number>(assignments.docs.map((doc: QueryDocumentSnapshot) => [
                doc.id, Array.isArray(doc.data().students) ? doc.data().students.length : 0
            ]));
            const teacherNames = new Map<string, string>(teachers.docs.map((doc: QueryDocumentSnapshot) => [
                doc.id, doc.data().name ?? ""
            ]));

            // 다른 지점으로 이동한 강사도 기존 학급에 표시하되, 같은 강사는 한 번만 조회한다.
            const missingTeacherIds = Array.from(new Set(doc_list.map(doc => doc.data().teacher as string)))
                .filter(uid => uid && !teacherNames.has(uid));
            await Promise.all(missingTeacherIds.map(async uid => {
                const teacher = await readFirestoreOneDoc("User/Auth/Teacher", uid);
                teacherNames.set(uid, teacher.exists() ? teacher.data().name ?? "" : "");
            }));

            const list = doc_list.map(doc => {
                const data = doc.data();
                return {
                    lesson_id: doc.id,
                    lesson_name: data.name,
                    teacher_uid: data.teacher,
                    teacher_name: teacherNames.get(data.teacher) ?? "",
                    student_count: studentCounts.get(doc.id) ?? 0
                };
            });

            setLoadingProgress(100);
            setLessonList(list);
            setItems(list);
        } catch (error) {
            console.error('Error fetching lesson list:', error);
            setLessonList([]);
            setItems([]);
        } finally {
            setIsLoading(false);
            setLoadingProgress(0);
        }
    }

    return {
        lessonList: filteredItems,
        searchKeyword,
        sortOrder,
        handleSearch,
        toggleSortOrder,
        isLoading,
        loadingProgress,
    };
};

export default useHomeworkClassView;
